import { BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';

import { ul } from './helpers';
import {
  EXTERNAL_PARTNERS_AND_ADVISERS_NAMES,
  WORK_EXPERIENCE_NAMES as EXTERNAL_PARTNERS_WORK_EXPERIENCE_NAMES,
  LIST_OF_EXTERNAL_PARTNERS_NAMES,
} from '../Sections/ManagementAndOrganization/ExternalPartners/utils/form';
import {
  INSTITUTION_NAMES,
  MANAGEMENT_TEAM_NAMES,
  PERSONNEL_PROFILES_NAMES,
  WORK_EXPERIENCE_NAMES,
} from '../Sections/ManagementAndOrganization/ManagementTeam/utils/form';
import {
  LEVEL2_DEPARTMENT_NAMES,
  LEVEL3_DEPARTMENT_NAMES,
  STAFF_ORGANIZATION_NAMES,
} from '../Sections/ManagementAndOrganization/StaffOrganization/utils/form';

const LIST_OF_INSTITUTIONS_INSTRUCTIONS = {
  [INSTITUTION_NAMES.NAME]: {
    name: 'Name of the institution',
    instruction: 'State the full names of educational institutions attended',
    exampleOne: 'American University',
    exampleTwo: 'Oklahoma State University',
  },
  [INSTITUTION_NAMES.SPECIALIZATION]: {
    name: 'Specialization',
    instruction: 'Specify area of specialization or major pursued during education of the key personnel',
    exampleOne: 'Engineering',
    exampleTwo: 'Finance',
  },
};

const WORK_EXPERIENCE_INSTRUCTIONS = {
  [WORK_EXPERIENCE_NAMES.EMPLOYER]: {
    name: 'Name of the employer',
    instruction: 'Clearly state the full name of the employer or organization',
    exampleOne: 'John Deere',
    exampleTwo: 'ARP Dend',
  },
  [WORK_EXPERIENCE_NAMES.POSITION]: {
    name: 'Position',
    instruction:
      'Specify job title or position of the key personnel held within the organization, highlighting role and responsibilities concisely and accurately',
    exampleOne: 'Chief engineer of the agricultural development department',
    exampleTwo: 'Finance manager',
  },
  [WORK_EXPERIENCE_NAMES.FROM]: {
    name: 'Period',
    instruction: 'Provide a detailed chronology of your employment history, indicating timeframes',
    exampleOne: '2000',
    exampleTwo: '2017',
  },
  [WORK_EXPERIENCE_NAMES.TO]: {
    name: 'Period',
    instruction: 'Provide a detailed chronology of your employment history, indicating timeframes',
    exampleOne: '2013',
    exampleTwo: '2023',
  },
  [WORK_EXPERIENCE_NAMES.FUNCTIONS]: {
    name: 'Functions',
    instruction:
      'Describe the responsibilities and areas of work in which this person was involved. Emphasis the importance of the employee`s actions in achieving the organization`s goals.',
    exampleOne:
      'Formulating and executing the company`s technology strategy in alignment with its overall business objectives. Leading the research and development efforts to innovate and enhance the company`s product offerings.',
    exampleTwo:
      'Developing and maintaining financial models to forecast the company`s financial performance. Analyzes financial data to provide insights into revenue trends, cost structures, and profitability. Assesses the company`s financial risks and develops strategies to mitigate them.',
  },
};

const PERSONNEL_PROFILES_INSTRUCTIONS = {
  [PERSONNEL_PROFILES_NAMES.FILE]: {
    name: '',
    instruction:
      'Upload the high-resolution image, ensure it meets professional standards and aligns with company branding.',
    exampleOne: '',
    exampleTwo: '',
  },
  [PERSONNEL_PROFILES_NAMES.NAME]: {
    name: 'Name',
    instruction: 'State full name of the key personnel in the format: First Name followed by Last Name',
    exampleOne: 'Oliver',
    exampleTwo: 'Mary',
  },
  [PERSONNEL_PROFILES_NAMES.SURNAME]: {
    name: 'Surname',
    instruction: 'State full name of the key personnel in the format: First Name followed by Last Name',
    exampleOne: 'Smith',
    exampleTwo: 'Wilson',
  },
  [PERSONNEL_PROFILES_NAMES.DATE_OF_BIRTH]: {
    name: 'Date of birth',
    instruction: 'Provide the date of birth in a clear and concise format, including day, month, and year',
    exampleOne: '01.01.1975',
    exampleTwo: '02.05.1983',
  },
  [PERSONNEL_PROFILES_NAMES.POSITION]: {
    name: 'Current position',
    instruction:
      'Specify job title or position of the key personnel held within the organization, highlighting role and responsibilities concisely and accurately',
    exampleOne: 'Chief technology officer',
    exampleTwo: 'Finance Manager',
  },
  [PERSONNEL_PROFILES_NAMES.LIST_OF_INSTITUTIONS]: LIST_OF_INSTITUTIONS_INSTRUCTIONS,
  [PERSONNEL_PROFILES_NAMES.WORK_EXPERIENCE]: WORK_EXPERIENCE_INSTRUCTIONS,
  [PERSONNEL_PROFILES_NAMES.KEY_SKILLS]: {
    name: 'Key skills',
    instruction:
      'List the critical skills for successful performance as a manager. Highlight competencies demonstrating capability in the assigned role.',
    exampleOne: ul([
      'Mechanical engineering',
      'Agricultural machinery design',
      'Project management',
      'Leadership',
      'Technical problem-solving',
    ]),
    exampleTwo: ul([
      'Financial planning and analysis',
      'Budgeting',
      'Forecasting',
      'Financial reporting',
      'Problem solving',
    ]),
  },
  [PERSONNEL_PROFILES_NAMES.ACHIEVEMENTS]: {
    name: 'Special achievements',
    instruction:
      'Introduce an overview of their key achievements within this area. Clearly define the specific expertise or specialization that sets them apart. Discuss their notable accomplishments, qualifications, or milestones that demonstrate a high level of proficiency in their field.',
    exampleOne:
      'At John Deere, he played a pivotal role in developing engine systems for Jan85 escalators. He participates in worldwide machinery exhibitions annually, including the International EXPO and Innovation in Chicago. He has consecutively topped Connecticut`s Promising Engineers ranking for five years.',
    exampleTwo:
      'Certified Chartered Financial Analyst (CFA). Mary has been recognized for her outstanding contributions to the field of Finance, receiving prestigious awards such as the Financial Excellence Award 2020 and the Finance Professional of the Year Award 2021.',
  },
};

const MANAGEMENT_TEAM_INSTRUCTIONS = {
  [MANAGEMENT_TEAM_NAMES.HISTORY]: {
    name: 'History and Background',
    instruction:
      'Detail the formation history of your management team. Describe the process of assembling the team, the key stages, roles, backgrounds and contributions of each team member. Highlight the expertise and experience brought by each member.',
    exampleOne:
      'The Company was founded by Americans John Johnson and Oliver Smith. John Johnson, a small agricultural company owner in Texas, partnered with Oliver Smith, an engineer at John Deere, a renowned agricultural, construction, and logging equipment manufacturer.',
    exampleTwo:
      'The critical project participants in the Company include John Johnson and Mary Wilson. The Company was founded by John Johnson, a graduate of A University majoring in programming. He partnered with Mary Wilson, a finance graduate from the same university, to establish the business.',
  },
  [MANAGEMENT_TEAM_NAMES.SKILLS]: {
    name: 'Skills and Competencies',
    instruction:
      "Provide the management team's key skills and competencies. Highlight the leadership and professional qualities that make your management team well-suited to lead the business. Focus on strengths contributing to the company's success.",
    exampleOne:
      'The management team`s extensive industry experience and business management and administration skills establish them as capable leaders. John Johnson`s entrepreneurial acumen and industry insights, combined with Oliver Smith`s technical expertise, provide the foundation for strategic direction and product innovation.',
    exampleTwo:
      'John Johnson`s programming skills are the foundation of the platform. He also designed the architecture of the first version. Mary Wilson`s experience in the financial departments of various companies has improved Marie`s skills in the financial management sector.',
  },
  [MANAGEMENT_TEAM_NAMES.PERSONAL_QUALITIES]: {
    name: 'Personal Qualities',
    instruction:
      'Describe the personal qualities of team members, focusing on soft skills, adaptability, resilience and collaboration. Emphasise the qualities critical to achieving the organisation`s goals and maintaining a competitive edge in the industry.',
    exampleOne:
      'The management team possesses a blend of leadership and professional qualities. Managing the company during the COVID-19 pandemic has underscored the importance of adaptability to external factors. The team`s proficiency in personal soft skills has facilitated staff management during crises and enabled them to devise solutions to new challenges.',
    exampleTwo:
      'Resilience, adaptability, collaboration, and innovation are pivotal personal qualities within the Company team. These qualities foster a dynamic work environment, enabling the Company to overcome challenges, embrace change, and continuously improve operations and solutions.',
  },
  [MANAGEMENT_TEAM_NAMES.PERSONNEL_PROFILES]: PERSONNEL_PROFILES_INSTRUCTIONS,
};

const EXTERNAL_PARTNERS_WORK_EXPERIENCE_INSTRUCTIONS = {
  [EXTERNAL_PARTNERS_WORK_EXPERIENCE_NAMES.EMPLOYER]: {
    name: 'Name of the employer',
    instruction: 'Clearly state the full name of the employer or organization',
    exampleOne: 'Lint',
    exampleTwo: 'A&Partners',
  },
  [EXTERNAL_PARTNERS_WORK_EXPERIENCE_NAMES.POSITION]: {
    name: 'Position',
    instruction:
      'Specify job title or position of the keypartners held within the organization, highlighting role and responsibilities concisely and accurately',
    exampleOne: 'Software developer',
    exampleTwo: 'Contracts lawyer',
  },
};

const LIST_OF_EXTERNAL_PARTNERS_INSTRUCTIONS = {
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.FILE]: {
    name: '',
    instruction:
      'Upload the high-resolution image, ensure it meets professional standards and aligns with company branding.',
    exampleOne: '',
    exampleTwo: '',
  },
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.NAME]: {
    name: 'Name',
    instruction: 'State full name of the key partners in the format: First Name followed by Last Name',
    exampleOne: 'Alex',
    exampleTwo: 'Camila',
  },
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.SURNAME]: {
    name: 'Surname',
    instruction: 'State full name of the key partners in the format: First Name followed by Last Name',
    exampleOne: 'Leto',
    exampleTwo: 'Devis',
  },
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.ROLE]: {
    name: 'Role in the company',
    instruction:
      'Specify job title or position of the key partners held within the organization, highlighting role and responsibilities concisely and accurately',
    exampleOne: 'IT consultant',
    exampleTwo: 'Legal advisor',
  },
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.WORK_EXPERIENCE]: EXTERNAL_PARTNERS_WORK_EXPERIENCE_INSTRUCTIONS,
  [LIST_OF_EXTERNAL_PARTNERS_NAMES.KEY_SKILLS]: {
    name: 'Key skills',
    instruction:
      'List the critical skills of your consultant. Highlight competencies demonstrating capability in the assigned role.',
    exampleOne: 'IT Strategy and Planning \nCybersecurity \nSoftware Development',
    exampleTwo: 'Legal Compliance \nIntellectual Property Protection \nLegal Training and Education',
  },
};

const EXTERNAL_PARTNERS_AND_ADVISERS_INSTRUCTIONS = {
  [EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.LIST_OF_EXTERNAL_PARTNERS]: LIST_OF_EXTERNAL_PARTNERS_INSTRUCTIONS,
  [EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.PARTNERS]: {
    name: 'Partner Expertise',
    instruction:
      'List all partners and consultants the company uses. Specify the expertise to which each partner contributes, highlighting their roles in aiding company objectives. Ensure clarity in showcasing how each partner`s skills benefit the company.',
    exampleOne:
      'The Company collaborates with IT consultants ABCD, which offers expertise in technology infrastructure, cybersecurity, and software development. ABCD assists the Company in implementing and maintaining robust IT systems and solutions.',
    exampleTwo:
      'The Сompany works with the legal advisor Camila Davis to ensure compliance with regulatory requirements and mitigate legal risks. The marketing agency ABC brings expertise in digital marketing, branding, and advertising, helping the Company reach its target audience.',
  },
  [EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.CRITERIA]: {
    name: 'Selection Criteria',
    instruction:
      'Establish selection criteria, such as successful projects, client testimonials, compatibility, industry reputation, or alignment with company goals. Emphasize that external consultants possess the specific skills and knowledge required to contribute meaningfully to the project.',
    exampleOne:
      'The most important criterion for choosing partners is expertise and experience. Partners should possess specialized expertise and extensive experience in relevant areas.',
    exampleTwo:
      'When choosing partners, the Company considers their experience, reputation, and cost-effectiveness. Partners should offer competitive pricing and cost-effective solutions that align with the Company`s budget. Partners must be reliable and trustworthy, capable of meeting deadlines, delivering on promises, and maintaining confidentiality.',
  },
  [EXTERNAL_PARTNERS_AND_ADVISERS_NAMES.PLANS]: {
    name: 'Expansion Plans',
    instruction:
      'Describe intentions to expand the roster of partners and advisors, including rationale, such as accessing new markets, enhancing expertise, or diversifying perspectives.  Highlight potential benefits and alignment with the company`s strategic objectives.',
    exampleOne:
      'The Company intends to expand its network of partners and advisors to leverage diverse expertise and insights. This expansion aims to strengthen strategic decision-making and enhance overall business performance.',
    exampleTwo:
      'The Company plans to onboard a financial advisor from an investment fund to optimize financial processes and aid in debt management in the future.',
  },
};

const LEVEL2_DEPARTMENT_INSTRUCTIONS = {
  [LEVEL2_DEPARTMENT_NAMES.DEPARTMENT]: {
    name: 'Organizational Structure',
    instruction:
      'Describe the overall organizational structure, emphasizing how different departments or units collaborate to achieve common goals.',
    exampleOne: '',
    exampleTwo: '',
  },
};

const LEVEL3_DEPARTMENT_INSTRUCTIONS = {
  [LEVEL3_DEPARTMENT_NAMES.DEPARTMENT]: {
    name: 'Organizational Structure',
    instruction:
      'Describe the overall organizational structure, emphasizing how different departments or units collaborate to achieve common goals.',
    exampleOne: '',
    exampleTwo: '',
  },
};

const STAFF_ORGANIZATION_INSTRUCTIONS = {
  [STAFF_ORGANIZATION_NAMES.NUMBER_OF_STAFF]: {
    name: 'Staff Number & Structure',
    instruction:
      'Provide a snapshot of the current employee count within the company. Specify the total number of employees and briefly outline the composition of the workforce, highlighting any key departments or teams.',
    exampleOne:
      'The Company currently employs 50 individuals, who are distributed across five departments: marketing and sales, Production, Human Resources, Finance, and Research and development.',
    exampleTwo:
      'The Company currently employs 12 individuals across 3 departments. The technical department is responsible for developing and maintaining the platform. The operational department is responsible for HR activities and marketing. The financial department is responsible for financial management.',
  },
  [STAFF_ORGANIZATION_NAMES.LEVEL1_DEPARTMENT]: {
    name: 'Organizational Structure',
    instruction:
      'Describe the overall organizational structure, emphasizing how different departments or units collaborate to achieve common goals.',
    exampleOne: '',
    exampleTwo: '',
  },
  [STAFF_ORGANIZATION_NAMES.LEVEL2_DEPARTMENT]: LEVEL2_DEPARTMENT_INSTRUCTIONS,
  [STAFF_ORGANIZATION_NAMES.LEVEL3_DEPARTMENT]: LEVEL3_DEPARTMENT_INSTRUCTIONS,
  [STAFF_ORGANIZATION_NAMES.SKILLS]: {
    name: 'Skills and Qualifications',
    instruction:
      'List the skills and qualifications you believe are necessary for your company`s employees to perform their roles. Include technical skills, education and relevant experience. Be concise and specific.',
    exampleOne:
      'Critical that employees have a degree in Mechanical Engineering or equivalent, experience in heavy equipment design, problem-solving ability, knowledge of agricultural practices, practical communication skills, experience working in a team, and familiarity with environmental regulations.',
    exampleTwo:
      'Platform developers should be experts in programming languages like Python, JavaScript, or Java. They also need experience in software development, proficiency in database management systems, and familiarity with cloud computing platforms like AWS or Azure.',
  },
  [STAFF_ORGANIZATION_NAMES.ADDITIONAL_HIRES]: {
    name: 'Hiring Needs',
    instruction:
      'Identify the types and numbers of staff required to implement the project. Outline the roles, skills, and expertise needed to support the project`s development.',
    exampleOne:
      'The project requires hiring 15 new employees. Recruiting 5 development engineers with experience in agricultural machinery design. Hiring 10 technical specialists for the Production department is essential to support increased manufacturing demands.',
    exampleTwo:
      'The planned staff growth includes adding a Project Coordinator role to support the project`s development. This individual will coordinate project activities and manage documentation. The Project Coordinator will possess strong organizational and communication skills and be able to multitask.',
  },
  [STAFF_ORGANIZATION_NAMES.APPROACHES]: {
    name: 'Recruitment Approaches',
    instruction:
      'Describe the company`s recruitment strategies. Introduce the individuals responsible for the hiring process. Provide a list of the methods used to attract and select candidates to ensure a thorough and efficient hiring process.',
    exampleOne:
      'The Human Resources Department will be responsible for the hiring process. The Company employs various tools to streamline and enhance the hiring process, including applicant tracking systems to manage job postings, candidate applications, and communication throughout the recruitment lifecycle.',
    exampleTwo:
      'The HR Manager, Theodore Swift, will oversee the recruitment and onboarding process. The HR Manager will utilize online job portals and recruitment software to attract and screen candidates efficiently.',
  },
  [STAFF_ORGANIZATION_NAMES.ATTRACTING_DIFFICULTIES]: {
    name: 'Attraction Difficulties',
    instruction:
      'Identify challenges in staff attraction. These may include skills mismatches, a competitive job market, and a scarcity of qualified candidates. Briefly describe all the barriers that may affect the hiring process.',
    exampleOne:
      'Recruitment challenges include skill mismatches, limited talent pools, and competition from other sectors. Technical expertise is crucial, but sourcing qualified candidates remains challenging, especially in rural areas.',
    exampleTwo:
      'The company is experiencing challenges in staff attraction, such as intense competition in the job market for skilled professionals. Talented specialists have higher salary requirements and benefits packages compared to other professionals.',
  },
  [STAFF_ORGANIZATION_NAMES.SALARY_POLICY]: {
    name: 'Salary and Motivation',
    instruction:
      'Detail your salary policy relative to the market. Describe existing motivation programs, including bonuses, profit sharing, and recognition initiatives. Outline any performance measures or targets associated with incentives.',
    exampleOne:
      'The Company`s salary policy is to offer above-market average salaries to attract top talent. Additionally, profit-sharing programs are in place to incentivize employees. Recognition initiatives and targets tied to performance measures further motivate staff.',
    exampleTwo:
      'The Company`s salary policy aligns with market standards, offering competitive compensation packages. Motivation programs include performance-based bonuses. Incentives are tied to individual and team performance metrics, ensuring alignment with company goals.',
  },
  [STAFF_ORGANIZATION_NAMES.RETENTION_DIFFICULTIES]: {
    name: 'Retention Challenges',
    instruction:
      'Identify the challenges associated with retention. Describe the strategies you use to increase retention, such as competitive compensation, professional development opportunities, an inclusive workplace culture, employee recognition programmes, etc.',
    exampleOne:
      'The Company is facing a challenge heavy physical demands and seasonal fluctuations in workload. To improve retention, we offer competitive compensation, professional development, an inclusive culture, recognition programmes and work-life balance.',
    exampleTwo:
      'Retention challenges include job dissatisfaction and aggressive hiring policies of competing companies. The Company`s strategies include offering competitive compensation, providing professional development opportunities, and fostering an inclusive workplace culture.',
  },
  [STAFF_ORGANIZATION_NAMES.DEVELOPMENT_PROGRAMS]: {
    name: 'Training Programs',
    instruction:
      'Describe in detail the staff development and training programmes in place. Indicate the training methods used for both new and existing employees. Emphasise the importance of developing employees` skills and their contribution to the organization`s objectives.',
    exampleOne:
      'The Company will implement training and development plans to prepare our staff to perform effectively. New employees will undergo thorough onboarding. Employees will have access to continuous learning opportunities, including workshops, seminars, webinars, and online courses.',
    exampleTwo:
      'The Company is committed to implementing a comprehensive training strategy. Regular workshops will be conducted to enhance staff skills in project management, communication and customer service. Employees will have access to external courses and certifications to develop their expertise further.',
  },
};

export const MANAGEMENT_AND_ORGANIZATION_INSTRUCTIONS = {
  [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION.MANAGEMENT_TEAM]: MANAGEMENT_TEAM_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION.EXTERNAL_PARTNERS_AND_ADVISERS]:
    EXTERNAL_PARTNERS_AND_ADVISERS_INSTRUCTIONS,
  [BUSINESS_TOOL_SUB_SECTIONS.MANAGEMENT_AND_ORGANIZATION.STAFF_ORGANIZATION]: STAFF_ORGANIZATION_INSTRUCTIONS,
};
